import { Controller } from "@hotwired/stimulus"
import { debounce } from "../application"

// Drives the reviews filter modal: refreshes the live "Show N reviews" count as filters change, and
// (on web) applies the selected filters by navigating the reviews page. Native applies via the
// applied-filters endpoint with the current form state.
export default class extends Controller {
    static targets = ["form"]
    static values = { reviewsPath: String, appliedFiltersPath: String }

    connect() {
        this.debouncedSubmit = debounce(() => this.formTarget.requestSubmit(), 200)
        this.formTarget.addEventListener("change", this.handleChange)
    }

    disconnect() {
        this.formTarget.removeEventListener("change", this.handleChange)
    }

    handleChange = (event) => {
        if (event.target.classList.contains("filter-input")) {
            this.showCountLoading()
            this.debouncedSubmit()
        }
    }

    clear() {
        this.formTarget.querySelectorAll('input[type="checkbox"].filter-input').forEach((input) => {
            input.checked = false
        })
        const allVersions = this.formTarget.querySelector("#version_all")
        if (allVersions) {
            allVersions.checked = true
        }
        this.showCountLoading()
        this.debouncedSubmit()
    }

    // Swap the "Show N reviews" count for the pulsing dots while the count frame reloads. The fresh
    // footer that replaces this one renders with the count shown and the dots hidden again.
    showCountLoading() {
        document.querySelectorAll(".show-reviews-count").forEach((el) => el.classList.add("hidden"))
        document.querySelectorAll(".show-reviews-loading").forEach((el) => el.classList.remove("hidden"))
    }

    // Web apply: read the live form state (including the Alpine-bound search/sort hidden inputs) and
    // navigate the reviews page with the selected filters.
    apply() {
        const params = new URLSearchParams(new FormData(this.formTarget))
        Turbo.visit(`${this.reviewsPathValue}?${params.toString()}`, { action: "advance" })
    }

    // Native apply: post the current form state so the server can append filter_cache and redirect
    // back to the reviews page. This avoids submitting stale params from the last count-frame render.
    applyNative() {
        const form = document.createElement("form")
        form.method = "post"
        form.action = this.appliedFiltersPathValue
        form.dataset.turboFrame = "_top"

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
        const csrfParam = document.querySelector('meta[name="csrf-param"]')?.content || "authenticity_token"
        if (csrfToken) {
            this.appendHiddenField(form, csrfParam, csrfToken)
        }

        new FormData(this.formTarget).forEach((value, key) => {
            this.appendHiddenField(form, key, value)
        })

        document.body.appendChild(form)
        form.requestSubmit()
    }

    appendHiddenField(form, name, value) {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = name
        input.value = value
        form.appendChild(input)
    }
}
